import { AddFillIcon } from "@equipmentshare/ds2-icons"
import { Stack, SvgIcon, Typography, Button, Box } from "@mui/material"
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"

import BulkPartSelection from "./BulkPartSelection"
import { LabelGeneratorFormValues, BulkPartOption } from "./LabelGenerator"

export const Generator: React.FC = () => {
	const {
		control,
		formState: { isValid, errors },
		trigger,
	} = useFormContext<LabelGeneratorFormValues>()

	const { fields, append, remove } = useFieldArray({
		control,
		name: "bulkPartSelection",
	})

	const bulkPartSelection = useWatch<LabelGeneratorFormValues, "bulkPartSelection">({
		control: control,
		name: "bulkPartSelection",
		defaultValue: [],
	})

	// Check if form is valid for button disable state
	const hasErrors = Object.keys(errors).length > 0
	const allPartsSelected = (bulkPartSelection || []).every((item) => item.part !== null)
	const isFormValid = isValid && !hasErrors && allPartsSelected

	// Calculate totalCount reactively, converting string counts to numbers
	const totalCount = (bulkPartSelection || []).reduce((total: number, item: BulkPartOption) => {
		const count = typeof item?.count === "string" ? parseInt(item.count, 10) : item?.count
		return total + (isNaN(count) || count === null ? 0 : count)
	}, 0)

	const handleRemove = async (index: number) => {
		remove(index)
		await trigger("bulkPartSelection")
	}

	return (
		<Box
			sx={{
				border: "1px solid",
				borderColor: "divider",
				borderRadius: "12px",
				width: "100%",
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
				minWidth: 0,
			}}
		>
			<Box sx={{ p: "12px", display: "flex", flex: 1, flexDirection: "column", gap: "1rem", minHeight: 0 }}>
				<Stack direction="row" spacing={1} alignItems="center">
					<SvgIcon component={AddFillIcon} sx={{ marginRight: "8px" }} />
					<Typography variant="h6" fontWeight="bold">
						Generator
					</Typography>
				</Stack>
				<Stack sx={{ maxHeight: "600px", overflow: "auto" }}>
					{fields.map((part, index) => (
						<BulkPartSelection
							key={`${part.id}-${index}`}
							name={`bulkPartSelection[${index}]`}
							index={index}
							deleteable={fields.length > 1}
							onDelete={() => handleRemove(index)}
						/>
					))}
				</Stack>
				<Button
					variant="outlined"
					fullWidth
					onClick={() => append({ part: null, count: 1 })}
					startIcon={<SvgIcon component={AddFillIcon} />}
				>
					Add Item
				</Button>
			</Box>

			<Button
				variant="contained"
				color="primary"
				fullWidth
				type="submit"
				disabled={!isFormValid}
				sx={{
					borderRadius: 0,
					fontWeight: 700,
					py: 1.5,
					m: 0,
					alignSelf: "stretch",
				}}
			>
				Generate {totalCount > 1 ? totalCount : ""} ID{totalCount === 1 ? "" : "s"}
			</Button>
		</Box>
	)
}
export default Generator
